import { useState } from 'react';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AcordoDrawer } from './AcordoDrawer';
import { useAcordos, useDeleteAcordo, type AcordoTipo } from '@/lib/hooks/entidades';

interface Props {
  workspaceId: string;
}

const TIPO_LABEL: Record<AcordoTipo, string> = {
  COLABORACAO: 'Colaboração',
  FOMENTO: 'Fomento',
  COOPERACAO: 'Cooperação',
  AUXILIO: 'Auxílio',
  SUBVENCAO: 'Subvenção',
};

export function AcordosCard({ workspaceId }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; numero: string } | null>(null);

  const { data, isLoading } = useAcordos(workspaceId);
  const deleteMut = useDeleteAcordo(workspaceId);

  const acordos = data?.items ?? [];

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success('Acordo removido.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover acordo.');
    } finally {
      setDeleteTarget(null);
    }
  }

  return (
    <>
      <Card variant="default" className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2 bg-mint-soft text-mint-deep shrink-0">
              <FileText className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div>
              <Eyebrow>Convênios</Eyebrow>
              <h3 className="font-display text-[16px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
                Acordos
              </h3>
              <p className="text-sm text-fg-muted mt-0.5">
                Termos de colaboração, fomento e instrumentos similares.
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setDrawerOpen(true)}
            className="shrink-0"
          >
            <Plus className="h-4 w-4" strokeWidth={1.8} />
            Adicionar
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="h-16 rounded-2 bg-card-soft animate-pulse"
              />
            ))}
          </div>
        ) : acordos.length === 0 ? (
          <div className="rounded-2 border border-dashed border-card-stroke bg-card-soft p-6 text-center">
            <p className="text-sm text-fg-muted">Nenhum acordo cadastrado ainda.</p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={() => setDrawerOpen(true)}
            >
              Cadastrar primeiro acordo
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-card-stroke">
            {acordos.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="flex items-start gap-2 min-w-0">
                  <Pill tone="mint" className="shrink-0 mt-0.5">
                    {TIPO_LABEL[a.tipo]}
                  </Pill>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-fg">
                      {a.numero}
                    </p>
                    <p className="text-xs text-fg-muted truncate mt-0.5">
                      {a.orgaoConcedente}
                      {(a.dataInicio ?? a.dataFim) && (
                        <span className="ml-2">
                          {formatDate(a.dataInicio)} — {formatDate(a.dataFim)}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  aria-label={`Remover acordo ${a.numero}`}
                  className="grid h-8 w-8 place-items-center rounded-2 text-fg-dim hover:text-warn hover:bg-warn-soft transition-colors shrink-0"
                  onClick={() => setDeleteTarget({ id: a.id, numero: a.numero })}
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <AcordoDrawer
        workspaceId={workspaceId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        title="Remover acordo?"
        description={`O acordo "${deleteTarget?.numero}" será removido permanentemente. Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        tone="danger"
        loading={deleteMut.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
